/**
 * SalMakes — Configurateur de collier
 * ------------------------------------
 * Logique client du configurateur : sélection de la chaîne, du nombre de
 * charms, composition des emplacements, calcul du prix total (100% basé
 * sur les vrais prix Shopify des variantes) et ajout au panier en un seul
 * appel /cart/add.js contenant plusieurs lignes partageant un `_bundle_id`
 * commun (chaîne + charms). Le regroupement visuel de ces lignes dans le
 * panier est géré séparément par assets/salmakes-cart-bundle-grouping.js.
 *
 * Ce fichier ne dépend d'aucune librairie externe.
 */
(function () {
  'use strict';

  // -------------------------------------------------------------------
  // Pop-up de zoom — partagée par toutes les instances du configurateur
  // (une seule section par page dans l'usage prévu, mais ça reste
  // sans risque si plusieurs sont présentes). Créée à la demande, au
  // premier clic sur une photo.
  // -------------------------------------------------------------------
  var lightboxEl = null;

  function closeLightbox() {
    if (!lightboxEl) return;
    lightboxEl.classList.remove('is-open');
    var img = lightboxEl.querySelector('[data-sm-lightbox-img]');
    if (img) img.removeAttribute('src');
  }

  function ensureLightbox() {
    if (lightboxEl) return lightboxEl;

    lightboxEl = document.createElement('div');
    lightboxEl.className = 'sm-lightbox';
    lightboxEl.innerHTML =
      '<figure class="sm-lightbox__figure">' +
        '<button type="button" class="sm-lightbox__close" data-sm-lightbox-close aria-label="Fermer">✕</button>' +
        '<img data-sm-lightbox-img alt="">' +
      '</figure>';
    document.body.appendChild(lightboxEl);

    lightboxEl.addEventListener('click', function (evt) {
      if (evt.target === lightboxEl || evt.target.closest('[data-sm-lightbox-close]')) {
        closeLightbox();
      }
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && lightboxEl.classList.contains('is-open')) {
        closeLightbox();
      }
    });

    return lightboxEl;
  }

  function openLightbox(src, alt) {
    if (!src) return;
    var el = ensureLightbox();
    var img = el.querySelector('[data-sm-lightbox-img]');
    if (img) {
      img.src = src;
      img.alt = alt || '';
    }
    el.classList.add('is-open');
  }

  function initBuilder(root) {
    var sectionId = root.dataset.sectionId;
    var dataScript = root.querySelector('script[data-sm-builder-data]');
    if (!dataScript) return;

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (err) {
      console.error('[SalMakes builder] JSON invalide dans la section', err);
      return;
    }

    var settings = data.settings;
    var chains = data.chains || [];
    var charms = data.charms || [];

    var els = {
      typeChoice: root.querySelector('[data-sm-type-choice]'),
      postType: root.querySelectorAll('[data-sm-requires-type]'),
      chainList: root.querySelector('[data-sm-chain-list]'),
      chainVariantPicker: root.querySelector('[data-sm-chain-variant-picker]'),
      qtyValue: root.querySelector('[data-sm-qty-value]'),
      qtyMinus: root.querySelector('[data-sm-qty-minus]'),
      qtyPlus: root.querySelector('[data-sm-qty-plus]'),
      qtyHint: root.querySelector('[data-sm-qty-hint]'),
      filters: root.querySelector('[data-sm-charm-filters]'),
      slots: root.querySelector('[data-sm-slots]'),
      charmList: root.querySelector('[data-sm-charm-list]'),
      necklaceName: root.querySelector('[data-sm-necklace-name]'),
      step4Title: root.querySelector('[data-sm-step4-title]'),
      previewChain: root.querySelector('[data-sm-preview-chain]'),
      previewCharms: root.querySelector('[data-sm-preview-charms]'),
      summaryTitle: root.querySelector('[data-sm-summary-title]'),
      summaryHint: root.querySelector('[data-sm-summary-hint]'),
      summaryLines: root.querySelector('[data-sm-summary-lines]'),
      summaryTotal: root.querySelector('[data-sm-summary-total]'),
      addToCartBtn: root.querySelector('[data-sm-add-to-cart]'),
      feedback: root.querySelector('[data-sm-feedback]')
    };

    var state = {
      productType: null, // 'necklace' | 'bracelet', choisi à l'étape préliminaire
      chain: chains[0] || null,
      chainVariant: chains[0] ? pickVariantForType(chains[0], null) : null,
      qty: 0,
      slots: [], // array of { charm, variant } | null
      activeFilter: 'all',
      necklaceName: ''
    };

    var carouselsInited = false;

    // ---------------------------------------------------------------------
    // Textes [type] — remplacement selon le collier/bracelet choisi
    // ---------------------------------------------------------------------
    function capitalize(word) {
      return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    }

    function currentLimits() {
      var fallback = { min: 0, max: 10, default: 0 };
      if (!state.productType || !settings.charmLimits) return fallback;
      return settings.charmLimits[state.productType] || fallback;
    }

    function applyTemplate(str, extra) {
      if (!str) return str;
      var word = state.productType && settings.typeWords ? settings.typeWords[state.productType] || '' : '';
      var tokens = { type: word, Type: capitalize(word) };
      if (extra) {
        for (var key in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, key)) tokens[key] = extra[key];
        }
      }
      return str.replace(/\[(\w+)\]/g, function (match, key) {
        return Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match;
      });
    }

    // ---------------------------------------------------------------------
    // Étape préliminaire — Collier ou bracelet
    // ---------------------------------------------------------------------
    if (els.typeChoice) {
      els.typeChoice.addEventListener('click', function (evt) {
        var btn = evt.target.closest('[data-sm-type-option]');
        if (!btn) return;
        selectType(btn.dataset.smTypeOption);
      });
    }

    function selectType(type) {
      state.productType = type;

      if (els.typeChoice) {
        Array.prototype.forEach.call(els.typeChoice.querySelectorAll('[data-sm-type-option]'), function (b) {
          var isSelected = b.dataset.smTypeOption === type;
          b.classList.toggle('is-selected', isSelected);
          b.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      }

      resizeSlots(currentLimits().default);
      renderSlots();
      renderCharmSelectionState();

      // Le prix d'une chaîne peut différer selon collier/bracelet (variantes
      // Shopify dédiées) : on met à jour la variante retenue, le prix
      // affiché sur chaque carte de la galerie, et le sélecteur de variante
      // en conséquence du type qui vient d'être choisi.
      if (state.chain) {
        state.chainVariant = pickVariantForType(state.chain, state.productType);
      }
      updateChainCardPrices();
      renderChainVariantPicker();

      Array.prototype.forEach.call(els.postType, function (el) {
        el.hidden = false;
      });

      updateTypeTexts();
      renderSummary();

      if (!carouselsInited) {
        carouselsInited = true;
        initCarousels();
      }

      clearFeedback();
    }

    function updateTypeTexts() {
      var limits = currentLimits();
      var templates = settings.templates || {};

      if (els.qtyHint) els.qtyHint.textContent = applyTemplate(templates.step2Hint, { min: limits.min, max: limits.max });
      if (els.step4Title) els.step4Title.textContent = applyTemplate(templates.step4Title);
      if (els.necklaceName) els.necklaceName.placeholder = applyTemplate(templates.namingPlaceholder);
      if (els.summaryTitle) els.summaryTitle.textContent = applyTemplate(templates.summaryTitle);
      if (els.summaryHint) els.summaryHint.textContent = applyTemplate(templates.summaryHint);
      if (els.addToCartBtn) els.addToCartBtn.textContent = applyTemplate(templates.ctaLabel);
    }

    // ---------------------------------------------------------------------
    // Carrousels (galerie de chaînes / galerie de charms)
    // ---------------------------------------------------------------------
    function debounce(fn, delay) {
      var t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, delay);
      };
    }

    function initCarousels() {
      var carousels = root.querySelectorAll('[data-sm-carousel]');
      Array.prototype.forEach.call(carousels, function (carousel) {
        var track = carousel.querySelector('.sm-carousel__track');
        var prevBtn = carousel.querySelector('[data-sm-carousel-prev]');
        var nextBtn = carousel.querySelector('[data-sm-carousel-next]');
        if (!track) return;

        function updateButtons() {
          var maxScroll = track.scrollWidth - track.clientWidth;
          if (prevBtn) prevBtn.disabled = track.scrollLeft <= 4;
          if (nextBtn) nextBtn.disabled = maxScroll <= 4 || track.scrollLeft >= maxScroll - 4;
        }

        function scrollByPage(direction) {
          track.scrollBy({ left: track.clientWidth * 0.8 * direction, behavior: 'smooth' });
        }

        if (prevBtn) prevBtn.addEventListener('click', function () { scrollByPage(-1); });
        if (nextBtn) nextBtn.addEventListener('click', function () { scrollByPage(1); });
        track.addEventListener('scroll', debounce(updateButtons, 50));
        window.addEventListener('resize', debounce(updateButtons, 150));
        updateButtons();
      });
    }

    // ---------------------------------------------------------------------
    // Rendu initial des filtres (à partir des tags produits des charms)
    // ---------------------------------------------------------------------
    if (els.filters) {
      var tagSet = {};
      charms.forEach(function (c) {
        (c.tags || []).forEach(function (t) {
          tagSet[t] = true;
        });
      });
      Object.keys(tagSet).sort().forEach(function (tag) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sm-filter-pill';
        btn.dataset.smFilter = tag;
        btn.textContent = tag;
        els.filters.appendChild(btn);
      });

      els.filters.addEventListener('click', function (evt) {
        var btn = evt.target.closest('[data-sm-filter]');
        if (!btn) return;
        state.activeFilter = btn.dataset.smFilter;
        Array.prototype.forEach.call(els.filters.querySelectorAll('[data-sm-filter]'), function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        applyCharmFilter();
      });
    }

    // ---------------------------------------------------------------------
    // Sélection de la chaîne
    // ---------------------------------------------------------------------
    if (els.chainList) {
      els.chainList.addEventListener('click', function (evt) {
        var zoomTrigger = evt.target.closest('[data-sm-zoom-trigger]');
        if (zoomTrigger) {
          evt.stopPropagation();
          var zoomCard = zoomTrigger.closest('[data-product-id]');
          var zoomProduct = zoomCard && chains.find(function (c) {
            return String(c.productId) === zoomCard.dataset.productId;
          });
          if (zoomProduct) openLightbox(zoomProduct.zoomImage || zoomProduct.image, zoomProduct.title);
          return;
        }

        var btn = evt.target.closest('[data-sm-chain-option]');
        if (!btn || btn.disabled) return;
        var product = chains.find(function (c) {
          return String(c.productId) === btn.dataset.productId;
        });
        if (!product) return;

        state.chain = product;
        state.chainVariant = pickVariantForType(product, state.productType);

        Array.prototype.forEach.call(els.chainList.querySelectorAll('[data-sm-chain-option]'), function (b) {
          var isSelected = b === btn;
          b.classList.toggle('is-selected', isSelected);
          b.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        renderChainVariantPicker();
        renderSummary();
      });
    }

    function renderChainVariantPicker() {
      if (!els.chainVariantPicker) return;
      els.chainVariantPicker.innerHTML = '';
      var product = state.chain;
      if (!product || !product.variants || product.variants.length <= 1) {
        els.chainVariantPicker.hidden = true;
        return;
      }

      // On ne propose ici que les variantes correspondant au type déjà
      // choisi (collier/bracelet) — ex. si la chaîne a des variantes
      // "Doré / Collier" et "Doré / Bracelet", on ne montre pas les
      // variantes bracelet quand le client compose un collier. S'il n'y a
      // pas de distinction de ce genre sur cette chaîne, toutes les
      // variantes restent proposées comme avant.
      var relevantVariants = variantsForType(product, state.productType);
      if (relevantVariants.length <= 1) {
        els.chainVariantPicker.hidden = true;
        return;
      }
      els.chainVariantPicker.hidden = false;

      var select = document.createElement('select');
      select.setAttribute('aria-label', 'Choisir une variante de chaîne');
      relevantVariants.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.title + (v.available ? '' : ' — ' + settings.soldOutLabel);
        opt.disabled = !v.available;
        if (state.chainVariant && String(state.chainVariant.id) === String(v.id)) {
          opt.selected = true;
        }
        select.appendChild(opt);
      });
      select.addEventListener('change', function () {
        state.chainVariant = relevantVariants.find(function (v) {
          return String(v.id) === select.value;
        });
        renderSummary();
      });
      els.chainVariantPicker.appendChild(select);
    }

    // ---------------------------------------------------------------------
    // Stepper quantité de charms
    // ---------------------------------------------------------------------
    function resizeSlots(qty) {
      var limits = currentLimits();
      var min = limits.min || 0;
      var max = limits.max || 10;
      qty = Math.max(min, Math.min(max, qty));
      state.qty = qty;

      var newSlots = [];
      for (var i = 0; i < qty; i++) {
        newSlots.push(state.slots[i] || null);
      }
      state.slots = newSlots;

      if (els.qtyValue) els.qtyValue.textContent = String(qty);
      if (els.qtyMinus) els.qtyMinus.disabled = qty <= min;
      if (els.qtyPlus) els.qtyPlus.disabled = qty >= max;
    }

    if (els.qtyMinus) {
      els.qtyMinus.addEventListener('click', function () {
        resizeSlots(state.qty - 1);
        renderSlots();
        renderSummary();
      });
    }
    if (els.qtyPlus) {
      els.qtyPlus.addEventListener('click', function () {
        resizeSlots(state.qty + 1);
        renderSlots();
        renderSummary();
      });
    }

    // ---------------------------------------------------------------------
    // Emplacements (slots) de charms
    // ---------------------------------------------------------------------
    function renderSlots() {
      if (!els.slots) return;
      els.slots.innerHTML = '';
      state.slots.forEach(function (slot, index) {
        var chip = document.createElement('div');
        chip.className = 'sm-slot' + (slot ? ' is-filled' : '');

        var thumb = document.createElement('span');
        thumb.className = 'sm-slot__thumb';
        if (slot) {
          var img = document.createElement('img');
          img.src = slot.charm.image || '';
          img.alt = slot.charm.title;
          thumb.appendChild(img);
        }
        chip.appendChild(thumb);

        var label = document.createElement('span');
        label.textContent = slot ? slot.charm.title : 'Emplacement ' + (index + 1) + ' — vide';
        chip.appendChild(label);

        if (slot) {
          var removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'sm-slot__remove';
          removeBtn.setAttribute('aria-label', 'Retirer ce charm');
          removeBtn.textContent = '✕';
          removeBtn.addEventListener('click', function () {
            state.slots[index] = null;
            renderSlots();
            renderCharmSelectionState();
            renderSummary();
          });
          chip.appendChild(removeBtn);
        }

        els.slots.appendChild(chip);
      });
    }

    function firstEmptySlotIndex() {
      for (var i = 0; i < state.slots.length; i++) {
        if (!state.slots[i]) return i;
      }
      return -1;
    }

    function countCharmInSlots(productId) {
      return state.slots.filter(function (s) {
        return s && String(s.charm.productId) === String(productId);
      }).length;
    }

    // ---------------------------------------------------------------------
    // Grille des charms
    // ---------------------------------------------------------------------
    if (els.charmList) {
      els.charmList.addEventListener('click', function (evt) {
        var zoomTrigger = evt.target.closest('[data-sm-zoom-trigger]');
        if (zoomTrigger) {
          evt.stopPropagation();
          var zoomCard = zoomTrigger.closest('[data-product-id]');
          var zoomCharm = zoomCard && charms.find(function (c) {
            return String(c.productId) === zoomCard.dataset.productId;
          });
          if (zoomCharm) openLightbox(zoomCharm.zoomImage || zoomCharm.image, zoomCharm.title);
          return;
        }

        var btn = evt.target.closest('[data-sm-charm-option]');
        if (!btn || btn.disabled) return;
        var charm = charms.find(function (c) {
          return String(c.productId) === btn.dataset.productId;
        });
        if (!charm) return;

        var targetIndex = firstEmptySlotIndex();
        if (targetIndex === -1) {
          showFeedback('Tous les emplacements sont complets — retire un charm pour en changer.', 'error');
          return;
        }

        var variant = pickFirstAvailableVariant(charm);
        state.slots[targetIndex] = { charm: charm, variant: variant };

        renderSlots();
        renderCharmSelectionState();
        renderSummary();
        clearFeedback();
      });
    }

    function renderCharmSelectionState() {
      if (!els.charmList) return;
      Array.prototype.forEach.call(els.charmList.querySelectorAll('[data-sm-charm-option]'), function (btn) {
        var count = countCharmInSlots(btn.dataset.productId);
        btn.classList.toggle('is-in-necklace', count > 0);
      });
    }

    function applyCharmFilter() {
      if (!els.charmList) return;
      Array.prototype.forEach.call(els.charmList.querySelectorAll('[data-sm-charm-option]'), function (btn) {
        if (state.activeFilter === 'all') {
          btn.hidden = false;
          return;
        }
        var tags = (btn.dataset.tags || '').split(',');
        btn.hidden = tags.indexOf(state.activeFilter) === -1;
      });
    }

    // ---------------------------------------------------------------------
    // Nom du collier
    // ---------------------------------------------------------------------
    if (els.necklaceName) {
      els.necklaceName.addEventListener('input', function () {
        state.necklaceName = els.necklaceName.value.trim();
      });
    }

    // ---------------------------------------------------------------------
    // Résumé + total
    // ---------------------------------------------------------------------
    function renderSummary() {
      var filledSlots = state.slots.filter(Boolean);
      var hasChain = !!(state.chain && state.chainVariant);

      if (els.summaryHint) {
        els.summaryHint.hidden = hasChain || filledSlots.length > 0;
      }

      if (els.previewChain) {
        if (hasChain && state.chain.image) {
          els.previewChain.src = state.chain.image;
          els.previewChain.alt = state.chain.title || '';
          els.previewChain.hidden = false;
        } else {
          els.previewChain.hidden = true;
          els.previewChain.removeAttribute('src');
        }
      }

      if (els.previewCharms) {
        els.previewCharms.innerHTML = '';
        filledSlots.forEach(function (slot) {
          var img = document.createElement('img');
          img.src = slot.charm.image || '';
          img.alt = slot.charm.title;
          els.previewCharms.appendChild(img);
        });
      }

      var total = 0;
      var lines = [];

      if (hasChain) {
        total += state.chainVariant.price;
        lines.push({ label: settings.chainRoleLabel + ' — ' + state.chain.title, price: state.chainVariant.price });
      }
      filledSlots.forEach(function (slot) {
        total += slot.variant ? slot.variant.price : 0;
        lines.push({ label: settings.charmRoleLabel + ' — ' + slot.charm.title, price: slot.variant ? slot.variant.price : 0 });
      });

      if (els.summaryLines) {
        els.summaryLines.innerHTML = '';
        if (lines.length) {
          els.summaryLines.hidden = false;
          lines.forEach(function (line) {
            var row = document.createElement('div');
            var label = document.createElement('span');
            label.textContent = line.label;
            var price = document.createElement('span');
            price.textContent = formatMoney(line.price, settings.moneyFormat);
            row.appendChild(label);
            row.appendChild(price);
            els.summaryLines.appendChild(row);
          });
        } else {
          els.summaryLines.hidden = true;
        }
      }

      if (els.summaryTotal) {
        els.summaryTotal.textContent = formatMoney(total, settings.moneyFormat);
      }

      var allSlotsFilled = filledSlots.length === state.slots.length;
      if (els.addToCartBtn) {
        els.addToCartBtn.disabled = !(state.productType && hasChain && allSlotsFilled && state.slots.length >= (currentLimits().min || 0));
      }
    }

    // ---------------------------------------------------------------------
    // Ajout au panier
    // ---------------------------------------------------------------------
    if (els.addToCartBtn) {
      els.addToCartBtn.addEventListener('click', function () {
        if (!state.productType) {
          showFeedback(settings.chooseTypeError, 'error');
          return;
        }
        if (!state.chain || !state.chainVariant) {
          showFeedback(settings.chooseChainError, 'error');
          return;
        }
        var filledSlots = state.slots.filter(Boolean);
        if (filledSlots.length !== state.slots.length) {
          showFeedback(settings.fillSlotsError, 'error');
          return;
        }

        addNecklaceToCart(filledSlots);
      });
    }

    function addNecklaceToCart(filledSlots) {
      var bundleId = generateBundleId();
      var necklaceLabel = state.necklaceName || applyTemplate((settings.templates || {}).bundleLabel);

      var items = [];

      items.push({
        id: state.chainVariant.id,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_type: state.productType,
          _bundle_role: settings.chainRoleLabel,
          _bundle_name: necklaceLabel,
          _bundle_position: '0'
        }
      });

      filledSlots.forEach(function (slot, index) {
        items.push({
          id: slot.variant.id,
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_type: state.productType,
            _bundle_role: settings.charmRoleLabel,
            _bundle_name: necklaceLabel,
            _bundle_position: String(index + 1)
          }
        });
      });

      els.addToCartBtn.disabled = true;
      els.addToCartBtn.classList.add('is-loading');
      clearFeedback();

      fetch(settings.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) {
              throw new Error(json.description || settings.addToCartError);
            }
            return json;
          });
        })
        .then(function () {
          showFeedback(applyTemplate((settings.templates || {}).addToCartSuccess), 'success');
          document.dispatchEvent(new CustomEvent('salmakes:necklace-added', { detail: { bundleId: bundleId } }));
          // Compatibilité meilleur-effort avec les tiroirs panier de thèmes OS 2.0 (ex. Dawn).
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { cart: cart } }));
            })
            .catch(function () {});
        })
        .catch(function (err) {
          showFeedback(err.message || settings.addToCartError, 'error');
        })
        .finally(function () {
          els.addToCartBtn.classList.remove('is-loading');
          renderSummary();
        });
    }

    // ---------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------
    function pickFirstAvailableVariant(product) {
      if (!product || !product.variants || !product.variants.length) return null;
      var available = product.variants.find(function (v) { return v.available; });
      return available || product.variants[0];
    }

    /**
     * Repère, parmi les variantes d'une chaîne, celle qui correspond au type
     * de bijou en cours (collier/bracelet) — utile quand une même chaîne a
     * un prix différent selon l'usage (ex. variantes "Collier" / "Bracelet",
     * la version bracelet consommant moins de matière). On matche sur le
     * titre de la variante ou sur ses options, en comparant au mot du type
     * (settings.typeWords). Si aucune variante ne correspond (chaîne "simple"
     * sans cette distinction), on retombe sur pickFirstAvailableVariant.
     */
    function normalizeForMatch(str) {
      return (str || '').toString().trim().toLowerCase();
    }

    function variantMatchesType(variant, word) {
      if (!word) return false;
      var title = normalizeForMatch(variant.title);
      if (title === word || title.indexOf(word) !== -1) return true;
      return (variant.options || []).some(function (opt) {
        var o = normalizeForMatch(opt);
        return o === word || o.indexOf(word) !== -1;
      });
    }

    function variantsForType(product, type) {
      if (!product || !product.variants) return [];
      var word = type && settings.typeWords ? normalizeForMatch(settings.typeWords[type]) : '';
      if (!word) return product.variants;
      var filtered = product.variants.filter(function (v) {
        return variantMatchesType(v, word);
      });
      return filtered.length ? filtered : product.variants;
    }

    function pickVariantForType(product, type) {
      if (!product || !product.variants || !product.variants.length) return null;
      var candidates = variantsForType(product, type);
      var available = candidates.find(function (v) { return v.available; });
      return available || candidates[0] || pickFirstAvailableVariant(product);
    }

    function updateChainCardPrices() {
      if (!els.chainList) return;
      chains.forEach(function (chain) {
        var card = els.chainList.querySelector('[data-sm-chain-option][data-product-id="' + chain.productId + '"]');
        if (!card) return;
        var priceEl = card.querySelector('.sm-chain-card__price');
        var variant = pickVariantForType(chain, state.productType);
        if (priceEl && variant) priceEl.textContent = formatMoney(variant.price, settings.moneyFormat);
      });
    }

    function generateBundleId() {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
      }
      return 'sm-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    }

    function showFeedback(message, type) {
      if (!els.feedback) return;
      els.feedback.textContent = message;
      els.feedback.classList.remove('is-error', 'is-success');
      els.feedback.classList.add(type === 'error' ? 'is-error' : 'is-success');
    }

    function clearFeedback() {
      if (!els.feedback) return;
      els.feedback.textContent = '';
      els.feedback.classList.remove('is-error', 'is-success');
    }

    /**
     * Formatte un montant en centimes selon shop.money_format.
     * Gère les jetons les plus courants ({{amount}}, {{amount_no_decimals}},
     * {{amount_with_comma_separator}}). Suffisant pour la plupart des thèmes ;
     * pour des formats très spécifiques, adapte cette fonction.
     */
    function formatMoney(cents, format) {
      var value = (cents / 100).toFixed(2);
      var parts = value.split('.');
      var noDecimals = parts[0];
      var withComma = parts[0] + ',' + parts[1];

      var output = format || '${{amount}}';
      output = output.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals);
      output = output.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma);
      output = output.replace(/\{\{\s*amount\s*\}\}/g, value);
      return output;
    }

    // ---------------------------------------------------------------------
    // Rendu initial
    // ---------------------------------------------------------------------
    // Les carrousels (initCarousels) ne sont initialisés qu'après le choix
    // du type de bijou (voir selectType), une fois que ces zones ne sont
    // plus "hidden" et ont donc une largeur mesurable.
    renderChainVariantPicker();
    renderSlots();
    renderSummary();
  }

  function init() {
    var roots = document.querySelectorAll('[id^="SmBuilderApp-"]');
    Array.prototype.forEach.call(roots, initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init si la section est rechargée dans l'éditeur de thème (Shopify design mode).
  document.addEventListener('shopify:section:load', function (evt) {
    var app = evt.target.querySelector('[id^="SmBuilderApp-"]');
    if (app) initBuilder(app);
  });
})();
